/**
 * Settings → Voice and approval — prototype VIEWS.settings, ST.tab==='voice' (docs/prototype.html L4424): one panel,
 * at most 620px wide, with the "Auto-approve confidence" range (60–99%) and the "Time to check before auto-approve"
 * select (1, 2 or 3 minutes), then two rules shown as switches.
 *
 * Data: GET /settings/thresholds (`capture.auto_threshold`, `capture.countdown_seconds`) and PATCH /settings/thresholds
 * with `{ capture: {...} }` and the shared config_version as If-Match (ADR §8.4). A 412 means someone else saved
 * first: their numbers load, and the person makes their change again.
 *
 * The two switches are rules the app always follows, not settings: every voice note is tied to the person signed in
 * (there is no shared link), and anyone may ask to hand over a task that is not theirs (a change request the Ops
 * associate director decides). They show as on and locked, with the reason, rather than as switches that save nothing.
 */
import { useState } from 'react';
import { CAPTURE_COUNTDOWN_CHOICES } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { useThresholds, useUpdateThresholds } from '../../lib/health';
import { dayMonth } from '../../lib/timesheets';
import { ErrorState, Loading, Switch, errorText, useToast } from '../../ui/kit';

const MINUTES: Record<number, string> = { 60: '1 minute', 120: '2 minutes', 180: '3 minutes' };

export function VoiceApproval() {
  const q = useThresholds();
  const save = useUpdateThresholds();
  const toast = useToast();
  const [auto, setAuto] = useState<number | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);

  if (q.isPending) return <Loading label="Loading voice and approval…" />;
  if (q.isError) return <ErrorState error={q.error} onRetry={() => q.refetch()} />;
  const { settings, config_version, updated_at, updated_by } = q.data;
  const cur = settings.capture;
  const a = auto ?? cur.auto_threshold;
  const c = countdown ?? cur.countdown_seconds;
  const changed = a !== cur.auto_threshold || c !== cur.countdown_seconds;
  const reset = () => (setAuto(null), setCountdown(null));

  const submit = () => {
    const capture = {
      ...(a !== cur.auto_threshold ? { auto_threshold: a } : {}),
      ...(c !== cur.countdown_seconds ? { countdown_seconds: c as 60 | 120 | 180 } : {}),
    };
    save.mutate(
      { version: config_version, body: { capture } },
      {
        onSuccess: () => {
          reset();
          toast('Saved. New voice notes use these from now on.');
        },
        onError: async (e) => {
          if (e instanceof ApiFailure && e.status === 412) {
            reset();
            await q.refetch();
            toast('Someone else changed Settings a moment ago. The page now shows their change. Make yours again.');
          } else toast(errorText(e));
        },
      },
    );
  };

  return (
    <div className="panel">
      <div className="bd" style={{ display: 'grid', gap: 20, maxWidth: 620 }}>
        <div>
          <div className="split">
            <b id="va-auto">Auto-approve confidence</b>
            <div className="grow" />
            <span className="mono">{a}%</span>
          </div>
          <input
            type="range"
            min={60}
            max={99}
            value={a}
            aria-labelledby="va-auto"
            style={{ width: '100%', marginTop: 8 }}
            onChange={(e) => setAuto(Number(e.target.value))}
          />
          <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
            Tasks below this wait in the review queue. Higher is safer.
          </div>
        </div>
        <label style={{ display: 'block' }}>
          <b>Time to check before auto-approve</b>
          <select className="in" style={{ marginTop: 6, maxWidth: 220, display: 'block' }} value={c} onChange={(e) => setCountdown(Number(e.target.value))}>
            {CAPTURE_COUNTDOWN_CHOICES.map((s) => (
              <option key={s} value={s}>
                {MINUTES[s] ?? `${s} seconds`}
              </option>
            ))}
          </select>
        </label>
        <div className="split">
          <div className="grow">
            <b>Personal voice link for each person</b>
            <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
              Each link works only for its owner, so every note is tied to a person. Always on.
            </div>
          </div>
          <Switch on onClick={() => undefined} label="Personal voice link for each person (always on)" disabled />
        </div>
        <div className="split">
          <div className="grow">
            <b>Let the person bounce a task that is not theirs</b>
            <div className="sub" style={{ color: 'var(--sub)', fontSize: 12.5 }}>
              They ask to hand it over and the Ops associate director decides. Always on.
            </div>
          </div>
          <Switch on onClick={() => undefined} label="Let the person bounce a task that is not theirs (always on)" disabled />
        </div>
        <div className="split" style={{ gap: 10, flexWrap: 'wrap', borderTop: '1px solid var(--line)', paddingTop: 14 }}>
          <span className="sub" style={{ fontSize: 12.5 }}>
            {updated_by && updated_at ? `Last changed by ${updated_by.name} on ${dayMonth(updated_at.slice(0, 10))}.` : 'These are the starting numbers.'}
          </span>
          <div className="grow" />
          {changed ? (
            <button className="btn" onClick={reset} disabled={save.isPending}>
              Discard
            </button>
          ) : null}
          <button className="btn pri" onClick={submit} disabled={!changed || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
